/*
PURPOSE: Global context system for ShareMusicPanel configuration and state management

USE CASES:
- Provide global ShareMusicPanel state that can be opened from any component
- Configure ShareMusicPanel behavior based on context (dashboard vs listening room)
- Manage panel visibility and content refresh callbacks across the application

DOMAIN BOUNDARIES:
- Responsible for ShareMusicPanel global state and configuration
- Provides interface for opening panel with context-specific settings
- Handles content refresh callbacks for different areas of the app
- Should NOT contain business logic for music resolution or content creation

ASSUMPTIONS:
- Used at route level to provide global panel access
- Configuration varies by page context (dashboard uses 'discussion', listening room uses 'listeningRoom')
- Panel should close after successful content creation in most contexts
*/

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useCopy } from '../hooks/useCopy';

// Configuration for different contexts where ShareMusicPanel is used
export interface ShareMusicConfig {
  headerText: string;
  explainerText: string;
  urlPlaceholder: string;
  defaultContentType: 'discussion' | 'review' | 'listeningRoom';
  onContentCreated?: () => void;
}

/**
 * Hook to get ShareMusicPanel configurations using centralized copy system
 * Provides dashboard and listeningRoom configs with copy from the centralized system
 */
export const useShareMusicConfigs = () => {
  const copy = useCopy();
  
  return {
    dashboard: {
      ...copy.common.shareMusicPanel.dashboard,
      defaultContentType: 'discussion' as const,
    },
    listeningRoom: {
      ...copy.common.shareMusicPanel.listeningRoom,
      defaultContentType: 'listeningRoom' as const,
    }
  } as const;
};

interface ShareMusicContextValue {
  isOpen: boolean;
  config: ShareMusicConfig | null;
  openPanel: (config: ShareMusicConfig) => void;
  closePanel: () => void;
}

const ShareMusicContext = createContext<ShareMusicContextValue | null>(null);

export const useShareMusicContext = (): ShareMusicContextValue => {
  const context = useContext(ShareMusicContext);
  if (!context) {
    throw new Error('useShareMusicContext must be used within a ShareMusicProvider');
  }
  return context;
};

interface ShareMusicProviderProps {
  children: ReactNode;
}

export const ShareMusicProvider: React.FC<ShareMusicProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ShareMusicConfig | null>(null);

  const openPanel = useCallback((newConfig: ShareMusicConfig) => {
    setConfig(newConfig);
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    // Keep config until next open to prevent flash of undefined
  }, []);

  const value: ShareMusicContextValue = {
    isOpen,
    config,
    openPanel,
    closePanel,
  };

  return (
    <ShareMusicContext.Provider value={value}>
      {children}
    </ShareMusicContext.Provider>
  );
};